/**
 * Generic opt-in order document router inputs (slug-discriminated create + slug-scoped prefill).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
import { z } from "zod";
import { ORDER_DOCUMENT_SLUG, OrderDocumentSlugSchema } from "~/shared/documents/order-document-settings";
import { OrderDocumentSourceTypeSchema } from "~/shared/documents/order-document-source";
import { BorderCanadianDocumentInputSchema } from "~/shared/documents/schemas/border-canada-input.schema";
import { BorderUsDocumentInputSchema, refineBorderUsDocumentIssuance } from "~/shared/documents/schemas/border-us-input.schema";
import { PickupInstructionsDocumentInputSchema } from "~/shared/documents/schemas/pickup-instructions-input.schema";
import { requiredUuid } from "~/utils/schemas";

export const OptInOrderDocumentPrefillInputSchema = z.object({
  id: requiredUuid,
  sourceType: OrderDocumentSourceTypeSchema.optional(),
  slug: OrderDocumentSlugSchema,
});

export type OptInOrderDocumentPrefillInput = z.infer<typeof OptInOrderDocumentPrefillInputSchema>;

/**
 * Print this one with the org's assigned template instead of the built-in document (SC-3349).
 *
 * Per member rather than once, because a discriminated union cannot take a shared extension — and
 * defaulting false on each keeps every existing caller on the legacy document.
 */
const printSourceFields = { useOutputTemplate: z.boolean().default(false) };

export const OptInOrderDocumentCreateInputSchema = z
  .discriminatedUnion("slug", [
    BorderUsDocumentInputSchema.extend({ slug: z.literal(ORDER_DOCUMENT_SLUG.borderUs), ...printSourceFields }),
    BorderCanadianDocumentInputSchema.extend({ slug: z.literal(ORDER_DOCUMENT_SLUG.borderCanada), ...printSourceFields }),
    PickupInstructionsDocumentInputSchema.extend({ slug: z.literal(ORDER_DOCUMENT_SLUG.pickupInstructions), ...printSourceFields }),
  ])
  .superRefine((input, ctx) => {
    if (input.slug === ORDER_DOCUMENT_SLUG.borderUs && !input.useOutputTemplate) {
      refineBorderUsDocumentIssuance(input, ctx);
    }
  });

export type OptInOrderDocumentCreateInput = z.infer<typeof OptInOrderDocumentCreateInputSchema>;
